import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createHashRouter, Navigate, RouterProvider } from "react-router-dom";
import { App } from "./App";
import { Loading } from "./components/ui";
import { lazyRoute } from "./lib/lazyRoute";
import { Dashboard } from "./routes/Dashboard";
import { Landing } from "./routes/Landing";
import "@fontsource-variable/inter";
import "./styles.css";

/*
 * Routes past the landing page and dashboard are code-split.
 *
 * The charting library is over half the bundle and is used on exactly one
 * route, so loading it eagerly makes every visitor pay for a chart most of
 * them never open. The landing page is where first-time visitors arrive and
 * the dashboard is where returning users deep-link, so both stay in the
 * initial chunk.
 *
 * lazyRoute rather than lazy: every deploy renames the chunks, and a tab that
 * was already open asks for a file that no longer exists. That surfaced as a
 * fatal error on the Model check tab after a redeploy.
 */
const Ticker = lazyRoute(() => import("./routes/Ticker").then((m) => ({ default: m.Ticker })));
const Themes = lazyRoute(() => import("./routes/Themes").then((m) => ({ default: m.Themes })));
const Portfolio = lazyRoute(() => import("./routes/Portfolio").then((m) => ({ default: m.Portfolio })));
const Personas = lazyRoute(() => import("./routes/Personas").then((m) => ({ default: m.Personas })));
const Sim = lazyRoute(() => import("./routes/Sim").then((m) => ({ default: m.Sim })));
const SimPortfolio = lazyRoute(() =>
  import("./routes/SimPortfolio").then((m) => ({ default: m.SimPortfolio })),
);
const Leaderboard = lazyRoute(() =>
  import("./routes/Leaderboard").then((m) => ({ default: m.Leaderboard })),
);
const Backtest = lazyRoute(() => import("./routes/Backtest").then((m) => ({ default: m.Backtest })));
const Method = lazyRoute(() => import("./routes/Method").then((m) => ({ default: m.Method })));
const Privacy = lazyRoute(() => import("./routes/Privacy").then((m) => ({ default: m.Privacy })));

const defer = (element: React.ReactNode) => (
  <Suspense fallback={<Loading what="this view" />}>{element}</Suspense>
);

/**
 * Hash routing.
 *
 * Static Web Apps can rewrite deep links to index.html, but the fallback also
 * has to exclude /api, /data and assets, and any mistake there shows up as a
 * blank page in production only. Hash routes cannot be misconfigured that way,
 * and this app has no SEO to lose.
 *
 * The landing page is a sibling of the app shell, not a child: it carries its
 * own minimal header (no tab bar, no ChatDock) while every application route
 * lives under the pathless <App /> layout.
 */
const router = createHashRouter([
  { path: "/", element: <Landing /> },
  {
    element: <App />,
    children: [
      { path: "dashboard", element: <Dashboard /> },
      { path: "ticker/:symbol", element: defer(<Ticker />) },
      { path: "themes", element: defer(<Themes />) },
      { path: "portfolio", element: defer(<Portfolio />) },
      { path: "personas", element: defer(<Personas />) },
      { path: "sim", element: defer(<Sim />) },
      { path: "sim/:portfolioId", element: defer(<SimPortfolio />) },
      { path: "leaderboard", element: defer(<Leaderboard />) },
      { path: "backtest", element: defer(<Backtest />) },
      { path: "method", element: defer(<Method />) },
      // The Ask page became the dock in App.tsx; old bookmarks land on the app.
      { path: "chat", element: <Navigate to="/dashboard" replace /> },
      { path: "privacy", element: defer(<Privacy />) },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
